import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Fab from '@mui/material/Fab'
import TextField from '@mui/material/TextField'
import RemoveIcon from '@mui/icons-material/Remove'
import type { ElementType } from 'react'

const getComponent = (source: unknown): ElementType => {
  if (source && typeof source === 'object' && 'default' in source) {
    return (source as { default: ElementType }).default
  }

  return source as ElementType
}

export const Counter = ({
  amount,
  setAmount,
  minimumAmount,
  maximumAmount,
  disabled,
}: {
  amount: number
  setAmount: (amount: number) => void
  minimumAmount?: number
  maximumAmount?: number
  disabled?: boolean
}) => {
  const AddIconComponent = getComponent(AddIcon)
  const RemoveIconComponent = getComponent(RemoveIcon)
  const increaseAmount = () => setAmount(amount + 1)
  const reduceAmount = () =>
    setAmount(
      minimumAmount === undefined
        ? amount - 1
        : Math.max(Number(minimumAmount), amount - 1)
    )

  // added this to avoid TS error when disabled is undefined
  // and prevent TS from wanting "??"" (which doesn't work) instead of "||"
  const isDisabled = disabled === true

  return (
    <Box className="flex items-center justify-end space-x-4">
      <Fab
        color="primary"
        aria-label="decrement points"
        data-testid="counter-decrement-button"
        size="small"
        onClick={reduceAmount}
        disabled={
          isDisabled ||
          (minimumAmount === undefined
            ? false
            : amount <= Number(minimumAmount))
        }
      >
        <RemoveIconComponent />
      </Fab>
      <TextField
        value={amount}
        disabled={isDisabled}
        sx={{ width: '60px', background: 'white' }}
        inputProps={{
          style: {
            padding: '8px 8px',
            textAlign: 'center',
            height: '40px',
          },
        }}
        onChange={(e) =>
          setAmount(
            minimumAmount === undefined
              ? Number(e.target.value.replace(/[^-0-9]/g, ''))
              : Math.max(
                  Number(minimumAmount),
                  Number(e.target.value.replace(/[^-0-9]/g, ''))
                )
          )
        }
      />
      <Fab
        color="primary"
        aria-label="increment points"
        data-testid="counter-increment-button"
        size="small"
        onClick={increaseAmount}
        disabled={
          isDisabled ||
          (maximumAmount === undefined
            ? false
            : amount === Number(maximumAmount))
        }
      >
        <AddIconComponent />
      </Fab>
    </Box>
  )
}
